import { MSG, parseEditQty } from '../../components/pickingEdit';
import { fmtQty, norm } from '../../format';
import type { FieldTarget } from '../../hooks/useServerErrors';
import type {
  CreateTransferPayload,
  FieldError,
  TransferLine,
  WarehouseStock,
} from '../../types';

/**
 * Pure model of the "Ajukan transfer" form: the draft rows (a source-bin stock
 * row + the qty typed for it), their checks, the POST body and the mapping of
 * a 422 back onto the form. No React here — StorageTransferNewPage renders it.
 */

export interface DraftLine {
  /** Stable React key (rows can be removed from the middle). */
  key: number;
  /** The source-bin stock row picked for this line (null = none yet). */
  stock: WarehouseStock | null;
  /** Raw input ("12", "1,5"). */
  qty: string;
}

let seq = 0;

export const newLine = (): DraftLine => ({ key: ++seq, stock: null, qty: '' });

export const SAME_STORAGE = 'Storage tujuan harus berbeda dari storage asal.';

export const TRANSFER_MSG = {
  source: 'Storage asal wajib dipilih.',
  dest: 'Storage tujuan wajib dipilih.',
  pickStock: 'Pilih produk & batch.',
  qtyRequired: 'Qty wajib diisi.',
  noLines: 'Pilih minimal satu produk untuk dipindahkan.',
  overAvailable: (n: number) => `Maksimal ${fmtQty(n)} tersedia di storage asal.`,
} as const;

/** The problem with a typed qty (nothing for an empty one), or null. */
export const qtyIssue = (l: DraftLine): string | null => {
  const raw = l.qty.trim().replace(',', '.');
  if (!raw) return null;
  const qty = parseEditQty(raw);
  if (qty === null) {
    return /^\d*\.\d{3,}$/.test(raw) ? MSG.qtyDecimals : MSG.qtyPositive;
  }
  if (qty <= 0) return MSG.qtyPositive;
  if (l.stock && qty > l.stock.qtyAvailable) {
    return TRANSFER_MSG.overAvailable(l.stock.qtyAvailable);
  }
  return null;
};

/** Problems of step 2, each pointing at its row (`lines[i].stock|qty`). */
export const checkLines = (lines: readonly DraftLine[]): FieldError[] => {
  const out: FieldError[] = [];
  lines.forEach((l, i) => {
    if (!l.stock) {
      out.push({
        field: `lines[${i}].stock`,
        message: TRANSFER_MSG.pickStock,
        lineIndex: i,
      });
      return;
    }
    const message = l.qty.trim() ? qtyIssue(l) : TRANSFER_MSG.qtyRequired;
    if (message)
      out.push({ field: `lines[${i}].qty`, message, lineIndex: i });
  });
  return out;
};

/** Problems of step 1. */
export const checkInfo = (sourceID: string, destID: string): FieldError[] => {
  const out: FieldError[] = [];
  if (!sourceID)
    out.push({ field: 'sourceStorageID', message: TRANSFER_MSG.source });
  if (!destID) out.push({ field: 'destStorageID', message: TRANSFER_MSG.dest });
  else if (destID === sourceID)
    out.push({ field: 'destStorageID', message: SAME_STORAGE });
  return out;
};

/** Qty of a valid row (0 while it is not). */
export const qtyOf = (l: DraftLine): number => parseEditQty(l.qty) ?? 0;

export const totalQty = (lines: readonly DraftLine[]): number =>
  Math.round(lines.reduce((t, l) => t + qtyOf(l), 0) * 100) / 100;

export const toPayload = (form: {
  hubID: number;
  hubName?: string;
  sourceStorageID: string;
  destStorageID: string;
  notes: string;
  lines: readonly DraftLine[];
}): CreateTransferPayload => ({
  hubID: form.hubID,
  ...(form.hubName ? { hubName: form.hubName } : {}),
  sourceStorageID: form.sourceStorageID,
  destStorageID: form.destStorageID,
  ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
  lines: form.lines.map((l) => ({
    sku: (l.stock as WarehouseStock).sku,
    batchNumber: (l.stock as WarehouseStock).batchNumber,
    // The picked row's type: the same batch can sit here as REGULAR and RETURN.
    stockType: (l.stock as WarehouseStock).stockType,
    qty: parseEditQty(l.qty) ?? Number(l.qty),
  })),
});

/** The review table's rows — same shape as a saved transfer line. */
export const reviewLines = (lines: readonly DraftLine[]): TransferLine[] =>
  lines.flatMap((l, i) =>
    l.stock
      ? [
          {
            lineNo: i + 1,
            stockID: l.stock.id,
            sku: l.stock.sku,
            itemName: l.stock.itemName,
            uomName: l.stock.uomName,
            batchNumber: l.stock.batchNumber,
            expiredDate: l.stock.expiredDate,
            stockType: l.stock.stockType,
            qty: qtyOf(l),
          },
        ]
      : []
  );

/* ---------------------------- server errors ------------------------------ */

export type TransferFieldKey = 'source' | 'dest' | 'notes' | 'lines' | 'form';

const STEP_OF: Record<TransferFieldKey, number> = {
  source: 1,
  dest: 1,
  notes: 1,
  lines: 2,
  form: 3,
};
export const transferStepOf = (k: TransferFieldKey): number => STEP_OF[k];

/** The line row an error names (`lineIndex`, else `lines[2].…`), or null. */
const lineOf = (e: FieldError): number | null => {
  if (e.lineIndex !== undefined && e.lineIndex !== null) return e.lineIndex;
  const m = /lines?\W*(\d+)/i.exec(e.field || '');
  return m ? Number(m[1]) : null;
};

/** Map a backend field (snake, dotted or camel) onto a transfer form field. */
export const transferFieldOf = (
  e: FieldError
): FieldTarget<TransferFieldKey> => {
  const n = norm(e.field || '');
  if (!n) return { key: 'form' };
  const line = lineOf(e);
  if (
    line !== null ||
    n.startsWith('line') ||
    ['sku', 'qty', 'batchnumber', 'stocktype'].includes(n)
  )
    return { key: 'lines', line };
  if (n.includes('source')) return { key: 'source' };
  if (n.includes('dest')) return { key: 'dest' };
  if (n === 'notes') return { key: 'notes' };
  return { key: 'form' };
};

/** Machine codes the server answers with that belong to one field. */
export const TRANSFER_CODE_FIELD: Readonly<Record<string, string>> = {
  INVALID_PARENT: 'destStorageID',
};
